// Live client-side mirror of `backend/container_planner_service.us_highway_axles`
// so the US Scale Ticket can recompute instantly as an admin edits the Truck
// & Chassis panel — no round-trip to the planner. Physics stays in ONE PLACE:
// when the panel is saved the backend re-runs the plan with the same specs.

export const TRUCK_SPEC_DEFAULTS = {
  tare_steer_lb: 9500,
  tare_drive_lb: 15500,
  tare_trailer_lb: 10500,
  axle_span_in: 426,
  front_overhang_in: 42,
  fifth_wheel_lead_in: 8,
  tractor_wheelbase_in: 232,
};

const KG_TO_LB = 2.20462262;
const LB_TO_KG = 1.0 / KG_TO_LB;
const LIMITS = { steer: 12000, drive: 34000, trailer: 34000, gvw: 80000 };

export function resolveTruckSpecs(overrides) {
  const out = { ...TRUCK_SPEC_DEFAULTS };
  Object.keys(TRUCK_SPEC_DEFAULTS).forEach((k) => {
    const raw = overrides ? overrides[k] : undefined;
    if (raw === undefined || raw === null || raw === "") return;
    const v = Number(raw);
    if (!Number.isFinite(v)) return;
    out[k] = v;
  });
  if (out.tractor_wheelbase_in <= 0) out.tractor_wheelbase_in = TRUCK_SPEC_DEFAULTS.tractor_wheelbase_in;
  if (out.axle_span_in <= 0) out.axle_span_in = TRUCK_SPEC_DEFAULTS.axle_span_in;
  return out;
}

function _row(actual_lb, limit_lb) {
  const over = Math.max(0, actual_lb - limit_lb);
  return {
    actual_lb: Math.round(actual_lb),
    actual_kg: Math.round(actual_lb * LB_TO_KG),
    limit_lb: Math.round(limit_lb),
    limit_kg: Math.round(limit_lb * LB_TO_KG),
    over_lb: Math.round(over),
    over_kg: Math.round(over * LB_TO_KG),
    ok: over <= 0.5,
  };
}

// `placement` = plan.placement — list of stacks each carrying `weight_kg` and
// `center_in`. Returns the same shape as the backend so the UI reads a single
// unified `us` object.
export function computeUsAxles(placement, specs) {
  const S = resolveTruckSpecs(specs);
  const steerShare = S.tractor_wheelbase_in > 0
    ? Math.max(0, Math.min(1, S.fifth_wheel_lead_in / S.tractor_wheelbase_in))
    : 0;

  let cargoLb = 0;
  let trailerCargoLb = 0;
  let driveCargoLb = 0;
  (placement || []).forEach((p) => {
    const wLb = Number(p.weight_kg || 0) * KG_TO_LB;
    cargoLb += wLb;
    const xDrive = Number(p.center_in || 0) - S.front_overhang_in;
    let shareTrailer;
    if (xDrive <= 0) shareTrailer = 0;
    else if (xDrive >= S.axle_span_in) shareTrailer = 1;
    else shareTrailer = xDrive / S.axle_span_in;
    trailerCargoLb += wLb * shareTrailer;
    driveCargoLb += wLb * (1 - shareTrailer);
  });

  const steerCargoLb = driveCargoLb * steerShare;
  const driveAfterLb = driveCargoLb * (1 - steerShare);

  const steerLb = S.tare_steer_lb + steerCargoLb;
  const driveLb = S.tare_drive_lb + driveAfterLb;
  const trailerLb = S.tare_trailer_lb + trailerCargoLb;
  const gvwLb = steerLb + driveLb + trailerLb;

  const steer = { axle: "steer", ..._row(steerLb, LIMITS.steer) };
  const drive = { axle: "drive", ..._row(driveLb, LIMITS.drive) };
  const trailer = { axle: "trailer", ..._row(trailerLb, LIMITS.trailer) };
  const gvw = { axle: "gvw", ..._row(gvwLb, LIMITS.gvw) };

  const violations = [];
  [steer, drive, trailer, gvw].forEach((a) => {
    if (a.ok) return;
    const label = a.axle === "gvw" ? "GVW" : (a.axle[0].toUpperCase() + a.axle.slice(1) + " axle");
    violations.push({
      axle: a.axle,
      actual_lb: a.actual_lb, limit_lb: a.limit_lb, over_lb: a.over_lb,
      actual_kg: a.actual_kg, limit_kg: a.limit_kg, over_kg: a.over_kg,
      message: `${label} overloaded: ${a.actual_lb.toLocaleString()} lb (limit ${a.limit_lb.toLocaleString()} lb — over by ${a.over_lb.toLocaleString()} lb).`,
    });
  });

  return {
    enabled: true,
    beta: true,
    constants: {
      tare_steer_lb: S.tare_steer_lb,
      tare_drive_lb: S.tare_drive_lb,
      tare_trailer_lb: S.tare_trailer_lb,
      axle_span_in: S.axle_span_in,
      front_overhang_in: S.front_overhang_in,
      fifth_wheel_lead_in: S.fifth_wheel_lead_in,
      tractor_wheelbase_in: S.tractor_wheelbase_in,
      steer_share_of_kingpin: Number(steerShare.toFixed(4)),
    },
    cargo_kg: Math.round(cargoLb * LB_TO_KG * 10) / 10,
    cargo_lb: Math.round(cargoLb),
    steer, drive, trailer, gvw,
    violations,
    over_axle: violations.some((v) => v.axle !== "gvw"),
    over_gvw: violations.some((v) => v.axle === "gvw"),
    compliant: violations.length === 0,
  };
}

// Panel definition — order matches the sidebar. `unit` renders next to the input.
export const TRUCK_SPEC_FIELDS = [
  { key: "tare_steer_lb",        label: "Tractor Steer Tare",              unit: "lbs" },
  { key: "tare_drive_lb",        label: "Tractor Drive Tare",              unit: "lbs" },
  { key: "tare_trailer_lb",      label: "Trailer Tandem Tare",             unit: "lbs" },
  { key: "axle_span_in",         label: "Axle Span (D)",                   unit: "in"  },
  { key: "front_overhang_in",    label: "Front Overhang",                  unit: "in"  },
  { key: "fifth_wheel_lead_in",  label: "Fifth Wheel Lead",                unit: "in", hint: "kingpin forward of drive-tandem centre — increase to shift weight onto STEER" },
  { key: "tractor_wheelbase_in", label: "Tractor Wheelbase (Steer→Drive)", unit: "in"  },
];


// =====================================================================
// US Highway Decision Suite — CoG-X + illustrative axle range across TWO
// chassis scenarios. Mirrors backend `compute_us_decision_suite` so the
// widget can live-recompute on any Truck Setup save without a plan re-run.
// =====================================================================
export const USHW_SCENARIO_A = { axle_span_in: 426, front_overhang_in: 42, label: "426in Span · 42in Overhang" };
export const USHW_SCENARIO_B = { axle_span_in: 480, front_overhang_in: 30, label: "480in Span · 30in Overhang" };
export const COGX = {
  target: 45, safeLow: 43, safeHigh: 47, absLow: 35, absHigh: 55,
};
export const DECISION_SUITE_LABEL =
  "Illustrative — chassis specs unverified, not sourced from an actual scale ticket.";

export function computeCogX(placement, contL) {
  const total = (placement || []).reduce((s, p) => s + Number(p.weight_kg || 0), 0);
  if (total <= 0 || !contL) {
    return {
      cog_x_in: 0, cog_x_pct: 0,
      target_pct: COGX.target, safe_low_pct: COGX.safeLow, safe_high_pct: COGX.safeHigh,
      abs_low_pct: COGX.absLow, abs_high_pct: COGX.absHigh,
      in_safe_window: false, in_absolute_window: false, band: "empty",
    };
  }
  const cog_in = (placement || []).reduce((s, p) => s + Number(p.weight_kg || 0) * Number(p.center_in || 0), 0) / total;
  const pct = 100 * cog_in / contL;
  const inSafe = pct >= COGX.safeLow && pct <= COGX.safeHigh;
  const inAbs = pct >= COGX.absLow && pct <= COGX.absHigh;
  return {
    cog_x_in: Number(cog_in.toFixed(1)),
    cog_x_pct: Number(pct.toFixed(2)),
    target_pct: COGX.target, safe_low_pct: COGX.safeLow, safe_high_pct: COGX.safeHigh,
    abs_low_pct: COGX.absLow, abs_high_pct: COGX.absHigh,
    in_safe_window: inSafe, in_absolute_window: inAbs,
    band: inSafe ? "green" : (inAbs ? "amber" : "red"),
  };
}

export function computeUsDecisionSuite(placement, contL, truckSpecs) {
  const cog = computeCogX(placement, contL);

  const runScenario = (geom) => {
    const merged = { ...(truckSpecs || {}), axle_span_in: geom.axle_span_in, front_overhang_in: geom.front_overhang_in };
    const ax = computeUsAxles(placement, merged);
    return { label: geom.label,
             geometry: { axle_span_in: geom.axle_span_in, front_overhang_in: geom.front_overhang_in },
             axles: ax, compliant: ax.compliant };
  };
  const A = runScenario(USHW_SCENARIO_A);
  const B = runScenario(USHW_SCENARIO_B);
  const ranges = {};
  ["steer", "drive", "trailer", "gvw"].forEach((k) => {
    const va = A.axles[k], vb = B.axles[k];
    const lo = Math.min(va.actual_lb, vb.actual_lb);
    const hi = Math.max(va.actual_lb, vb.actual_lb);
    ranges[k] = {
      min_lb: lo, max_lb: hi,
      min_kg: Math.round(lo / 2.20462262),
      max_kg: Math.round(hi / 2.20462262),
      limit_lb: va.limit_lb, limit_kg: va.limit_kg,
      any_over: !va.ok || !vb.ok,
      worst_over_lb: Math.max(va.over_lb || 0, vb.over_lb || 0),
    };
  });

  const bothFail = !A.compliant && !B.compliant;
  const eitherFail = !A.compliant || !B.compliant;

  let status, reasons = [];
  if (!cog.in_absolute_window) {
    status = "red";
    reasons.push(`CoG-X ${cog.cog_x_pct}% is outside the absolute ${COGX.absLow}-${COGX.absHigh}% window — the load is dangerously unbalanced.`);
  } else if (bothFail) {
    status = "red";
    reasons.push("Both illustrative chassis scenarios show axle overloads — no realistic rig can carry this load legally.");
  } else if (cog.in_safe_window && !eitherFail) {
    status = "green";
  } else if (cog.in_safe_window && eitherFail) {
    status = "amber";
    const which = !A.compliant ? "A" : "B";
    reasons.push(`Balanced plan, but flag for US scale check (scenario ${which} shows an axle overload).`);
  } else {
    status = "amber";
    const drift = cog.cog_x_pct < COGX.safeLow ? "nose-heavy" : "tail-heavy";
    reasons.push(`CoG-X ${cog.cog_x_pct}% is outside the safe ${COGX.safeLow}-${COGX.safeHigh}% window (${drift}).`);
  }

  return {
    enabled: true, beta: true,
    illustrative_label: DECISION_SUITE_LABEL,
    status, reasons,
    cog_x: cog,
    scenarios: { A, B },
    axle_ranges: ranges,
  };
}
